import React from 'react'
import { ConnectedFormGroup, ConnectedSelect } from 'components/FormElements'
import { Box, List, ListItem, SimpleGrid } from '@chakra-ui/react'
import {
  Suburb,
  useFlatCitiesQuery,
  useFlatDistrictsQuery,
  useFlatProvincesQuery,
  useSuburbsQuery
} from 'generated/graphql'
import { useFormikContext } from 'formik'
import { AddPropertyValues } from '.'
import { useGeolocation } from 'hooks'
import SearchBar from '../Listings/SearchBar'

const Location: React.FC<{
  isTabletOrMobile?: boolean
}> = ({ isTabletOrMobile }) => {
  const {
    setFieldValue,
    values: { Suburb }
  } = useFormikContext<AddPropertyValues>()
  const { countryCode, countryName } = useGeolocation()
  const [selectedSuburb, setSelectedSuburb] = React.useState<Suburb | null>(null)
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(undefined)
  const [showSuggestions, setShowSuggestions] = React.useState(false)

  React.useEffect(() => {
    if (selectedSuburb) {
      setFieldValue('Suburb', selectedSuburb?.id)
      setFieldValue('City', selectedSuburb?.city?.id)
      setFieldValue('District', selectedSuburb?.city?.district?.id)
      setFieldValue('Province', selectedSuburb?.city?.district?.province?.id)
    }
  }, [
    selectedSuburb?.id,
    selectedSuburb?.city?.id,
    selectedSuburb?.city?.district?.id,
    selectedSuburb?.city?.district?.province?.id,
    setFieldValue
  ])

  React.useEffect(() => {
    if (searchTerm) {
      setShowSuggestions(true)
    } else {
      setShowSuggestions(false)
    }
  }, [searchTerm])

  const { data: provincesData } = useFlatProvincesQuery({
    fetchPolicy: 'cache-first',
    variables: {
      limit: 50,
      where: {
        'country.name': countryName,
        'country.code': countryCode
      }
    }
  })

  const { data: districtsData } = useFlatDistrictsQuery({
    fetchPolicy: 'cache-first',
    variables: {
      limit: 50,
      where: {
        'province.country.code': countryCode,
        'province.country.name': countryName
      }
    }
  })
  const { data: citiesData } = useFlatCitiesQuery({
    fetchPolicy: 'cache-first',
    variables: {
      limit: 50,
      where: {
        'province.country.code': countryCode,
        'province.country.name': countryName
      }
    }
  })
  const { data: suburbData } = useSuburbsQuery({
    fetchPolicy: 'cache-first',
    variables: {
      where: {
        'city.province.country.code': countryCode,
        'city.province.country.name': countryName,
        ...(searchTerm && { name_contains: searchTerm })
      }
    }
  })

  return (
    <>
      <ConnectedFormGroup
        name="Street_address"
        placeholder="Harbour Arch Boulevard, Foreshore, Cape Town"
        label="Street address *"
      />
      <Box zIndex={60} position="relative">
        <SearchBar
          mb={2}
          mt={6}
          rounded="lg"
          onSearch={(text) => {
            setSearchTerm(text)
          }}
          placeholder="Search for a suburb"
        />
        {showSuggestions ? (
          <List
            bg="white"
            maxH="40vh"
            overflowY="auto"
            rounded="md"
            position="absolute"
            width="100%"
          >
            {suburbData?.suburbs?.map((suburb) => (
              <ListItem
                onClick={() => {
                  setSelectedSuburb(suburb as Suburb)
                  setShowSuggestions(false)
                }}
                p={2}
                cursor="pointer"
                fontSize="sm"
                transition="all 0.2s"
                _hover={{
                  bg: 'gray.100'
                }}
                key={suburb?.id}
              >
                {suburb?.name}
              </ListItem>
            ))}
          </List>
        ) : (
          <></>
        )}
      </Box>
      {selectedSuburb || Suburb ? (
        <SimpleGrid rowGap={0} columnGap={2} columns={isTabletOrMobile ? 0 : 2}>
          <ConnectedSelect
            name="Province"
            placeholder="Select a Province"
            label="Province *"
            onChange={() => {
              setFieldValue('District', '')
              setFieldValue('City', '')
              setFieldValue('Suburb', '')
            }}
            options={
              provincesData?.provinces?.map((province) => ({
                label: province?.name || '',
                value: province?.id || ''
              })) || []
            }
          />
          <ConnectedSelect
            name="District"
            placeholder="Select a District"
            label="District *"
            onChange={() => {
              setFieldValue('City', '')
              setFieldValue('Suburb', '')
            }}
            options={
              districtsData?.districts?.map((district) => ({
                label: district?.name || '',
                value: district?.id || ''
              })) || []
            }
          />
          <ConnectedSelect
            name="City"
            placeholder="Select a City"
            label="City *"
            onChange={() => {
              setFieldValue('Suburb', '')
            }}
            options={
              citiesData?.cities?.map((city) => ({
                label: city?.name || '',
                value: city?.id || ''
              })) || []
            }
          />
          <ConnectedSelect
            name="Suburb"
            placeholder="Select a Suburb"
            label="Suburb *"
            options={
              suburbData?.suburbs?.map((suburb) => ({
                label: suburb?.name || '',
                value: suburb?.id || ''
              })) || []
            }
          />
        </SimpleGrid>
      ) : (
        <></>
      )}
    </>
  )
}

export default Location
